import React, { useState, useEffect } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import {
    Button, Box, Typography, Paper, Alert,
    FormControlLabel, Switch, CircularProgress
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { collection, doc, query, where, getDocs, getDoc, addDoc } from 'firebase/firestore';
import { db } from '../firebase';

const QRScanner = ({ orgId, userId, onVerificationComplete }) => {
    const [html5QrCode, setHtml5QrCode] = useState(null);
    const [scanning, setScanning] = useState(false);
    const [useFrontCamera, setUseFrontCamera] = useState(false);
    const [error, setError] = useState(null);
    const [cameraReady, setCameraReady] = useState(null);
    const [debugLogs, setDebugLogs] = useState([]);
    const [verificationStatus, setVerificationStatus] = useState(null);

    const log = msg => setDebugLogs(d => [...d, `${new Date().toLocaleTimeString()}: ${msg}`]);

    useEffect(() => {
        const qr = new Html5Qrcode('qr-reader');
        setHtml5QrCode(qr);
        log('Scanner initialized (polyfilled mediaDevices.getUserMedia)');

        return () => {
            if (qr.isScanning) qr.stop().catch(() => log('Cleanup stop failed'));
        };
    }, []);

    const onScanSuccess = async (decodedText) => {
        log(`Decoded text: ${decodedText}`);
        try {
            await html5QrCode.stop();
            setScanning(false);
            log('Scanner stopped after success');
        } catch (e) {
            log(`Stop error: ${e.message}`);
        }
        // now verify & clock-in
        verifyQRCode(decodedText);
    };


    let lastFailureLog = 0;
    const onScanFailure = _ => {
        const now = Date.now();
        if (now - lastFailureLog > 1000) {
            log('Still no QR detected');
            lastFailureLog = now;
        }
    };

    const checkCameraPermission = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            stream.getTracks().forEach(t => t.stop());
            setCameraReady(true);
            log('Camera permission granted');
            return true;
        } catch (e) {
            setError('Camera access denied. Please allow camera and retry.');
            log(`Permission denied: ${e.message}`);
            setCameraReady(false);
            return false;
        }
    };

    const verifyQRCode = async (qrData) => {
        try {
            // 1) Parse the QR payload
            let content;
            try {
                content = JSON.parse(qrData);
            } catch {
                const url = new URL(qrData);
                content = {
                    orgId: url.searchParams.get('orgId'),
                    locationId: url.searchParams.get('locationId'),
                    codeId: url.searchParams.get('codeId'),
                };
            }

            // 2) Basic format checks
            if (!content.orgId || !content.locationId) {
                throw new Error('Invalid QR: missing required fields');
            }
            if (content.orgId !== orgId) {
                throw new Error('QR code is for a different organization');
            }

            // 3) Verify the location exists
            const locRef = doc(db, 'organizations', orgId, 'locations', content.locationId);
            const locSnap = await getDoc(locRef);
            if (!locSnap.exists()) {
                throw new Error('Location not found');
            }
            const locationData = locSnap.data();

            // 4) Verify the code isn’t expired
            if (content.codeId) {
                const codesRef = collection(db, 'organizations', orgId, 'qrCodes');
                const q = query(codesRef, where('codeId', '==', content.codeId));
                const snap = await getDocs(q);
                if (snap.empty) {
                    throw new Error('QR code not found');
                }
                const codeData = snap.docs[0].data();
                const expiresAt = codeData.expiresAt?.toDate?.() || new Date(codeData.expiresAt);
                if (new Date() > expiresAt) {
                    throw new Error('QR code has expired');
                }
            }

            // 5) Clock-in: add an attendance record
            const attendRef = collection(db, 'organizations', orgId, 'attendance');
            await addDoc(attendRef, {
                userId,
                locationId: content.locationId,
                codeId: content.codeId,
                timestamp: new Date(),
                method: 'qrCode',
            });

            // 6) Success UI & callback
            setVerificationStatus({
                success: true,
                message: `Clock-in successful at ${locationData.name}`
            });
            onVerificationComplete?.({
                method: 'qrCode',
                success: true,
                locationId: content.locationId,
                locationName: locationData.name
            });

        } catch (err) {
            // 7) Failure UI & callback
            log(`Verification failed: ${err.message}`);
            setVerificationStatus({
                success: false,
                message: err.message
            });
            onVerificationComplete?.({
                method: 'qrCode',
                success: false,
                error: err.message
            });
        }
    };


    const startScanner = async () => {
        if (!html5QrCode) return;
        const ok = await checkCameraPermission();
        if (!ok) return;

        setScanning(true);
        setError(null);
        log(`Starting scan (facingMode: ${useFrontCamera ? 'user' : 'environment'})`);

        try {
            const container = document.getElementById('qr-reader');
            const size = Math.min(container.clientWidth * 0.5, 300);  // center 50% ROI
            const config = {
                fps: 10,
                qrbox: { width: size, height: size },
                formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
                experimentalFeatures: { useBarCodeDetectorIfSupported: true }
            };

            await html5QrCode.start(
                { facingMode: useFrontCamera ? 'user' : 'environment' },
                config,
                onScanSuccess,
                onScanFailure
            );
            log('Scanner started successfully');
        } catch (e) {
            const msg = e.message || e.toString();
            setError(msg);
            log(`Scanner error: ${msg}`);
            console.error('Full scanner exception:', e);
            setScanning(false);
        }
    };

    const stopScanner = () => {
        if (html5QrCode && html5QrCode.isScanning) {
            html5QrCode.stop()
                .then(() => { setScanning(false); log('Scanner stopped'); })
                .catch(e => log(`Stop error: ${e.message}`));
        }
    };

    const toggleCamera = () => {
        if (scanning) stopScanner();
        setUseFrontCamera(f => !f);
        log(`Camera switched to ${!useFrontCamera ? 'front' : 'back'}`);
    };

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h6">QR Code Scanner</Typography>
            {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

            {verificationStatus && (
                <Alert severity={verificationStatus.success ? 'success' : 'error'} sx={{ my: 2 }}>
                    {verificationStatus.message}
                </Alert>
            )}

            <Box id="qr-reader" sx={{ width: '100%', height: 300, bgcolor: '#000', mb: 2 }} />

            <Box sx={{ textAlign: 'center', mb: 2 }}>
                {scanning
                    ? <Button variant="outlined" onClick={stopScanner}>Cancel Scan</Button>
                    : <Button
                        variant="contained"
                        startIcon={<QrCodeScannerIcon />}
                        onClick={startScanner}
                    >
                        Scan QR Code
                    </Button>
                }
            </Box>

            <FormControlLabel
                control={
                    <Switch
                        checked={useFrontCamera}
                        onChange={toggleCamera}
                        disabled={scanning}
                    />
                }
                label={useFrontCamera ? "Front Camera" : "Back Camera"}
            />

            <Typography sx={{ mt: 3 }} variant="subtitle2">Debug Logs:</Typography>
            <Box sx={{ maxHeight: 150, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {debugLogs.map((l, i) => <div key={i}>{l}</div>)}
            </Box>
        </Paper>
    );
};

export default QRScanner;




